import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faPlus, faSearch, faClose, faChevronRight, faChevronDown, faCamera, faUpload, faTimes, faPaperclip, faMagicWandSparkles } from "@fortawesome/free-solid-svg-icons";
import config from "../utils/config";
import MenuDropdown from "./MenuDropdown";
import SearchTabs from "./SearchTabs";
import * as amplitude from '@amplitude/analytics-browser'
import { Player } from '@lottiefiles/react-lottie-player';
import loader from '../loader/loading_animation.json'


// Define the dropdown options (copied from MenuBar.js)
const cuisineOptions = [
  { id: "mexican", label: "Mexican" },
  { id: "german", label: "German" },
  { id: "chinese", label: "Chinese" },
  { id: "american", label: "American" },
  { id: "italian", label: "Italian" },
  { id: "japanese", label: "Japanese" },
  { id: "spanish", label: "Spanish" },
  { id: "korean", label: "Korean" },
  { id: "moroccan", label: "Moroccan" },
];

const ingredientOptions = [
  { id: "beef", label: "Beef" },
  { id: "pork", label: "Pork" },
  { id: "chicken", label: "Chicken" },
  { id: "tofu", label: "Tofu" },
  { id: "eggs", label: "Eggs" },
  { id: "seafood", label: "Seafood" },
  { id: "vegetables", label: "Vegetables" },
  { id: "pasta", label: "Pasta" },
];

const mealOptions = [
  { id: "quick", label: "30 Minute Meals" },
  { id: "breakfast", label: "Breakfast/Brunch" },
  { id: "lunch", label: "Lunch" },
  { id: "dinner", label: "Dinner" },
  { id: "desserts", label: "Desserts" },
  { id: "healthy", label: "Healthy" },
];

const dietOptions = [
  { id: "vegan", label: "Vegan" },
  { id: "vegetarian", label: "Vegetarian" },
  { id: "keto", label: "Keto" },
  { id: "paleo", label: "Paleo" },
  { id: "dairy free", label: "Dairy-Free" },
  { id: "gluten free", label: "Gluten-Free" },
  { id: "kosher", label: "Kosher" },
];

const creatorOptions = [
  { id: "Chris De La Rosa", label: "Chris De La Rosa" }
];

const PublicMenuBar = () => {
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState("full"); // full or kitchen
  const [currentIngredient, setCurrentIngredient] = useState("");
  const [ingredientsList, setIngredientsList] = useState([]);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [showAnalysisAnimation, setShowAnalysisAnimation] = useState(false);
  const searchBarRef = useRef();
  const fileInputRef = useRef();
  const navigate = useNavigate();

  const handleSearchOnClick = () => {
    const addMarginTopAndAnimationClass = document.getElementById("mainId");
    setSearchBarVisible(!searchBarVisible);
    
    if (!searchBarVisible) {
      if (window.innerWidth < 1024) {
        addMarginTopAndAnimationClass.style.marginTop = "11rem";
      } else {
        addMarginTopAndAnimationClass.style.marginTop = "20rem";
      }
    } else {
      if (window.innerWidth < 1024) {
        addMarginTopAndAnimationClass.style.marginTop = "5rem";
      } else {
        addMarginTopAndAnimationClass.style.marginTop = "7rem";
      }
    }
  };

  const isUrlValid = (userInput) => {
    var res = userInput.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
    return res != null;
  };

  const handleSearchOnSubmit = async (e) => {
    const addMarginTopAndAnimation = document.getElementById("mainId");
    if (window.innerWidth < 1024) {
      addMarginTopAndAnimation.style.marginTop = "5rem";
    } else {
      addMarginTopAndAnimation.style.marginTop = "7rem";
    }

    let query;
    if (typeof e == "object") {
      e.preventDefault();
      query = e.target.value;
    } else {
      query = `${e}`;
    }
// Amplitude tracking
amplitude.logEvent('Recipe Search', {
  search_query: query,
  user_id: localStorage.getItem('userId') || 'guest',
  auth_status: localStorage.getItem('userId') ? 'authenticated' : 'unauthenticated',
});
    if (!isUrlValid(query)) {
      const url = query ? `/recipes?search=${query}` : `/recipes`;
      setSearchBarVisible(false);
      setSearchQuery("");
      navigate(url);
    }
  };

  const handleToggleSearchMode = () => {
    setSearchMode(prevMode => prevMode === "full" ? "kitchen" : "full");
    setSearchQuery("");
    setIngredientsList([]);
    setCurrentIngredient("");
  };

  const handleRemoveIngredient = (index) => {
    const updatedIngredientsList = [...ingredientsList];
    updatedIngredientsList.splice(index, 1);
    setIngredientsList(updatedIngredientsList);
  };

  const handleSearchByIngredients = () => {
    const addMarginTopAndAnimation = document.getElementById("mainId");
    if (window.innerWidth < 1024) {
      addMarginTopAndAnimation.style.marginTop = "5rem";
    } else {
      addMarginTopAndAnimation.style.marginTop = "7rem";
    }

    setSearchBarVisible(false);

    // Construct the query parameter from ingredientsList
    const queryParams = ingredientsList.length > 0
      ? `?ingredients=${ingredientsList.join(',')}`
      : "";

    // Navigate to the public version of the kitchen search page
    navigate(`/search${queryParams}`);
  };

  const handleMenuOptionClick = (menuId, optionId) => {
    setSearchBarVisible(false);
    
    if (menuId === "cuisines")
      navigate(`/recipes/cuisines/${optionId}`);
    if (menuId === "diets")
      navigate(`/recipes/diets/${optionId}`);
    if (menuId === "meals")
      navigate(`/recipes/meals/${optionId}`);
    if (menuId === "ingredients")
      navigate(`/recipes/ingredients/${optionId}`);
    if (menuId === "creators")
      navigate(`/creator/chris-de-la-rosa`);
  };

  const toggleMenu = () => {
    setIsMobileMenuVisible(!isMobileMenuVisible);
    setSearchBarVisible(false);
    setOpenSubmenu(null);
  };

  const toggleSubmenu = (index) => {
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  const handleMenuClick = () => {
    setSearchBarVisible(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileMenu = document.getElementById('mobile-menu');
      const menuButton = document.getElementById('mobile-menu-button');
      
      if (isMobileMenuVisible && mobileMenu && !mobileMenu.contains(event.target) && 
          menuButton && !menuButton.contains(event.target)) {
        setIsMobileMenuVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuVisible]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Show processing message for large files
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 5) {
        console.log('Processing large image, this may take a moment...');
      }

      setUploadedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        if (fileSizeInMB > 5) {
          console.log('Large image processed successfully!');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAnalyzeImage = async () => {
    if (!uploadedImage) return;
    
    setIsAnalyzingImage(true);
    setShowAnalysisAnimation(true);
    
    try {
      // Compress image before sending
      const compressedImage = await compressImage(uploadedImage);
      
      const response = await fetch(`${config.API_SERVER_URL}/api/v3/extract-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          base64Image: compressedImage
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Check for error response (improved error handling)
      if (!data.success || data.error) {
        console.error('API Error:', data.error);
        
        // Check for various error message formats
        const errorMessage = data.error || 'Unknown error';
        const isNotRecipeError = errorMessage.toLowerCase().includes('not a recipe') || 
                                errorMessage.toLowerCase().includes('not suitable') ||
                                errorMessage.toLowerCase().includes('no valid ingredients');
        
        if (isNotRecipeError) {
          alert('This image doesn\'t appear to contain recipe ingredients. Please upload an image of food ingredients or a recipe.');
        } else {
          alert(`Error: ${errorMessage}`);
        }
        
        setIsAnalyzingImage(false);
        setShowAnalysisAnimation(false);
        return;
      }
      
      // Extract ingredients from API response
      if (data.ingredients && Array.isArray(data.ingredients) && data.ingredients.length > 0) {
        const newIngredients = [...ingredientsList];
        
        // Process the clean ingredients from the improved backend
        data.ingredients.forEach(ingredientName => {
          // Remove any remaining quotes and clean the ingredient name
          const cleanIngredient = ingredientName
            .replace(/"/g, '') // Remove any quotes
            .trim()
            .toLowerCase();
          
          if (cleanIngredient && !newIngredients.includes(cleanIngredient)) {
            newIngredients.push(cleanIngredient);
          }
        });
        
        // Show success animation for 2 seconds then navigate
        setTimeout(() => {
          setIsAnalyzingImage(false);
          setShowAnalysisAnimation(false);
          setSearchBarVisible(false);
          
          // Navigate to search results with ingredients
          const queryParams = newIngredients.length > 0
            ? `?ingredients=${newIngredients.join(',')}`
            : "";
          
          navigate(`/search${queryParams}`);
        }, 2000);
        
        console.log('Raw API ingredients:', data.ingredients);
        console.log('Cleaned ingredients:', newIngredients);
        console.log('Final ingredients added:', newIngredients.filter(ing => !ingredientsList.includes(ing)));
      } else {
        console.warn('No ingredients found in API response');
        alert('No ingredients could be identified in this image. Please try a different image.');
      }
      
    } catch (error) {
      console.error('Error analyzing image:', error);
      if (error.message.includes('413') || error.message.includes('Payload Too Large')) {
        alert('Image is too large. Please try a smaller image or compress it first.');
      } else {
        alert('Failed to analyze image. Please check your connection and try again.');
      }
    }
  };

  // Helper function to compress image
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Start with aggressive compression for very large images
        let maxSize = 800;
        let quality = 0.7;
        
        // If original image is very large, be more aggressive
        if (img.width > 2000 || img.height > 2000) {
          maxSize = 600;
          quality = 0.5;
        }
        if (img.width > 4000 || img.height > 4000) {
          maxSize = 400;
          quality = 0.3;
        }
        
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Progressive compression - keep reducing quality until size is acceptable
        const tryCompress = (currentQuality) => {
          const compressedDataUrl = canvas.toDataURL('image/jpeg', currentQuality);
          
          // Check if compressed size is acceptable (aim for under 2MB base64)
          const sizeInBytes = (compressedDataUrl.length * 3) / 4;
          const sizeInMB = sizeInBytes / (1024 * 1024);
          
          if (sizeInMB > 2 && currentQuality > 0.1) {
            // Still too large, reduce quality further
            return tryCompress(currentQuality - 0.1);
          } else {
            // Size is acceptable or we've reached minimum quality
            resolve(compressedDataUrl);
          }
        };
        
        tryCompress(quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Helper function to extract ingredient name from full description
  const extractIngredientName = (ingredientLine) => {
    if (!ingredientLine || typeof ingredientLine !== 'string') return '';
    
    // Remove quantities and measurements, extract the main ingredient
    let ingredient = ingredientLine.toLowerCase().trim();
    
    // Remove common measurements and quantities
    ingredient = ingredient
      .replace(/^\d+(\.\d+)?\s*(cups?|tablespoons?|teaspoons?|tbsp|tsp|lbs?|pounds?|ounces?|oz|grams?|g|ml|liters?|l)\s*/i, '')
      .replace(/^\d+(\.\d+)?\s*/i, '') // Remove leading numbers
      .replace(/,.*$/, '') // Remove everything after comma (like ", sliced", ", softened")
      .replace(/\s+(sliced|diced|chopped|minced|softened|melted|fresh|dried|ground|whole|large|small|medium).*$/i, '')
      .trim();
    
    // Take the main ingredient (first few words)
    const words = ingredient.split(' ');
    if (words.length > 3) {
      ingredient = words.slice(0, 3).join(' ');
    }
    
    return ingredient;
  };

  return (
    <>
    <header className={`${searchBarVisible ? "search-active" : null} fixed left-1/2 transform -translate-x-1/2 bg-[#0e4621] px-4 xl:px-12 py-2 z-10 w-full`}>
      <div className="flex justify-between items-center">
        <Link to="/">
          <img className="logo" src="/Ladle_app_highres_rounded_square.png" alt="Logo" />
        </Link>

        {/* Desktop Navigation Menu */}
        <div className="hidden lg:flex items-center gap-4 flex-grow justify-center">
          <MenuDropdown
            id="cuisines"
            items={cuisineOptions}
            onClick={handleMenuClick}
            handleOptionClick={handleMenuOptionClick}
          >
            Cuisines
          </MenuDropdown>
          
          <span className="menuDivider hidden lg:block"></span>
          
          <MenuDropdown
            id="diets"
            items={dietOptions}
            onClick={handleMenuClick}
            handleOptionClick={handleMenuOptionClick}
          >
            Diets
          </MenuDropdown>
          
          <span className="menuDivider hidden lg:block"></span>
          
          <MenuDropdown
            id="meals"
            items={mealOptions}
            onClick={handleMenuClick}
            handleOptionClick={handleMenuOptionClick}
          >
            Meals
          </MenuDropdown>
          
          <span className="menuDivider hidden lg:block"></span>
          
          <MenuDropdown
            id="ingredients"
            items={ingredientOptions}
            onClick={handleMenuClick}
            handleOptionClick={handleMenuOptionClick}
          >
            Ingredients
          </MenuDropdown>
          
          <span className="menuDivider hidden lg:block"></span>
          
          <MenuDropdown
            id="creators"
            items={creatorOptions}
            onClick={handleMenuClick}
            handleOptionClick={handleMenuOptionClick}
          >
            Creators
          </MenuDropdown>
        
        </div>

        <div className="flex items-center gap-6">
          <FontAwesomeIcon 
            className="text-white text-xl cursor-pointer" 
            icon={faSearch} 
            onClick={handleSearchOnClick}
          />

          <div className="flex items-center gap-4">
            <Link to="/login">
              <button className="bg-white text-[#0e4621] px-4 py-2 rounded">Sign In</button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            id="mobile-menu-button"
            className="flex flex-col items-center justify-center w-10 h-8 space-y-2 rounded focus:outline-none lg:hidden"
            onClick={toggleMenu}
          >
            <span className="block w-6 h-0.5 bg-white"></span>
            <span className="block w-6 h-0.5 bg-white"></span>
            <span className="block w-6 h-0.5 bg-white"></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuVisible && (
        <div id="mobile-menu" className="lg:hidden bg-[#0e4621] text-white mt-4 py-2 px-4 rounded-md">
          <div className="flex flex-col space-y-2">
            {/* Cuisines */}
            <div className="border-b border-gray-600 py-2">
              <button 
                className="flex items-center justify-between w-full text-left py-2"
                onClick={() => toggleSubmenu(0)}
              >
                <span>Cuisines</span>
                <FontAwesomeIcon icon={openSubmenu === 0 ? faChevronDown : faChevronRight} />
              </button>
              
              {openSubmenu === 0 && (
                <div className="pl-4 mt-2 space-y-2">
                  {cuisineOptions.map((option) => (
                    <button 
                      key={option.id} 
                      className="block w-full text-left py-2 hover:text-gray-300"
                      onClick={() => {
                        handleMenuOptionClick("cuisines", option.id);
                        toggleMenu();
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Diets */}
            <div className="border-b border-gray-600 py-2">
              <button 
                className="flex items-center justify-between w-full text-left py-2"
                onClick={() => toggleSubmenu(1)}
              >
                <span>Diets</span>
                <FontAwesomeIcon icon={openSubmenu === 1 ? faChevronDown : faChevronRight} />
              </button>
              
              {openSubmenu === 1 && (
                <div className="pl-4 mt-2 space-y-2">
                  {dietOptions.map((option) => (
                    <button 
                      key={option.id} 
                      className="block w-full text-left py-2 hover:text-gray-300"
                      onClick={() => {
                        handleMenuOptionClick("diets", option.id);
                        toggleMenu();
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Meals */}
            <div className="border-b border-gray-600 py-2">
              <button 
                className="flex items-center justify-between w-full text-left py-2"
                onClick={() => toggleSubmenu(2)}
              >
                <span>Meals</span>
                <FontAwesomeIcon icon={openSubmenu === 2 ? faChevronDown : faChevronRight} />
              </button>
              
              {openSubmenu === 2 && (
                <div className="pl-4 mt-2 space-y-2">
                  {mealOptions.map((option) => (
                    <button 
                      key={option.id} 
                      className="block w-full text-left py-2 hover:text-gray-300"
                      onClick={() => {
                        handleMenuOptionClick("meals", option.id);
                        toggleMenu();
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Ingredients */}
            <div className="border-b border-gray-600 py-2">
              <button 
                className="flex items-center justify-between w-full text-left py-2"
                onClick={() => toggleSubmenu(3)}
              >
                <span>Ingredients</span>
                <FontAwesomeIcon icon={openSubmenu === 3 ? faChevronDown : faChevronRight} />
              </button>
              
              {openSubmenu === 3 && (
                <div className="pl-4 mt-2 space-y-2">
                  {ingredientOptions.map((option) => (
                    <button 
                      key={option.id} 
                      className="block w-full text-left py-2 hover:text-gray-300"
                      onClick={() => {
                        handleMenuOptionClick("ingredients", option.id);
                        toggleMenu();
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Creators */}
            <div className="border-b border-gray-600 py-2">
              <button 
                className="flex items-center justify-between w-full text-left py-2"
                onClick={() => toggleSubmenu(4)}
              >
                <span>Creators</span>
                <FontAwesomeIcon icon={openSubmenu === 4 ? faChevronDown : faChevronRight} />
              </button>
              
              {openSubmenu === 4 && (
                <div className="pl-4 mt-2 space-y-2">
                  {creatorOptions.map((option) => (
                    <button 
                      key={option.id} 
                      className="block w-full text-left py-2 hover:text-gray-300"
                      onClick={() => {
                        handleMenuOptionClick("creators", option.id);
                        toggleMenu();
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* What's in My Kitchen */}

          </div>
        </div>
      )}


{searchBarVisible && (
  <div ref={searchBarRef} className="absolute w-full left-0 top-full bg-gray-100 search-bar-container p-3">
    {showError && (
      <div className="text-center text-xl font-bold mt-3 text-red-700">
        URL is invalid
      </div>
    )}

    {/* Add the tab toggle */}
    <SearchTabs 
      activeTab={searchMode} 
      onTabChange={setSearchMode} 
    />

    {/* Regular Search Form */}
    {searchMode === "full" && (
      <form className="search-bar" onSubmit={(e) => e.preventDefault()}>
        <div className="input-container w-full flex flex-col sm:flex-row justify-center items-center">
          {loading ? (
            <div className="loading-icon">
              <img alt="" src="/loading.svg" />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row justify-center items-center w-full lg:w-auto">
              <div className="relative w-full lg:w-auto">
                <textarea
                  style={{
                    height: "50px",
                    width: "650px",
                    wordBreak: "break-word",
                    resize: "none",
                  }}
                  autoFocus
                  className="hidden lg:block px-5"
                  placeholder="Search anything, Search for specific ingredients"
                  value={searchQuery}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchOnSubmit(e.target.value);
                    }
                  }}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <input
                  type="text"
                  style={{
                    width: "100%",
                    maxWidth: "600px",
                    maxHeight: "300px",
                    display: window.innerWidth < 1024 ? "" : "none",
                  }}
                  autoFocus
                  className="px-3 py-2 rounded-sm border border-gray-300 focus:ring-2 focus:ring-green-500 transition w-full"
                  placeholder="Search anything, search for specific ingredients"
                  value={searchQuery}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchOnSubmit(e.target.value);
                    }
                  }}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="hidden lg:block">
                <button
                  disabled={searchQuery === ""}
                  onClick={() => handleSearchOnSubmit(searchQuery)}
                  className="lg:ml-3 h-10 bg-[#0e4621] hover:bg-[#3e8e41] text-white px-4 rounded"
                >
                  Search
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Keyword Suggestions for Full Search */}
        <div className="hidden lg:block text-center bg-gray-100 px-4 mt-4">
          <span style={{ opacity: "0.5" }}>Try these keywords</span>
          <div className="flex flex-col justify-center gap-2">
            <div className="mt-3">
              <span
                className="bg-gray-300 px-2 py-1 text-black rounded-[4px] cursor-pointer"
                onClick={() => {
                  setSearchQuery("Chicken ramen noodles");
                }}
              >
                <img
                  alt=""
                  src="/try-prompt.png"
                  className="inline h-4 mr-1 mb-1"
                />
                Chicken ramen noodles
              </span>
              <span
                className="bg-gray-300 px-2 py-1 ml-4 text-black rounded-[4px] cursor-pointer"
                onClick={() => {
                  setSearchQuery("Raspberry crumble bars");
                }}
              >
                <img
                  alt=""
                  src="/try-prompt.png"
                  className="inline h-4 mr-1 mb-1"
                />
                Raspberry crumble bars
              </span>
            </div>
            <div className="mt-3">
              <span
                className="bg-gray-300 px-2 py-1 text-black rounded-[4px] cursor-pointer"
                onClick={() => {
                  setSearchQuery("Chicken pot pie");
                }}
              >
                <img
                  alt=""
                  src="/try-prompt.png"
                  className="inline h-4 mr-1 mb-1"
                />
                Chicken pot pie
              </span>
            </div>
          </div>
        </div>
      </form>
    )}

    {/* Kitchen Search Form - Based on publicSearchIngredients.js UI */}
    {searchMode === "kitchen" && (
      <div className="max-w-2xl mx-auto mb-4">
        <form onSubmit={(e) => {
          e.preventDefault();
          handleSearchByIngredients();
        }} className="flex flex-col gap-4">
          {/* Input with Plus button */}
          <div className="relative flex mb-2">
            <input
              type="text"
              value={currentIngredient}
              onChange={(e) => setCurrentIngredient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Add ingredient logic
                  if (currentIngredient.trim() !== "") {
                    const ingredientList = currentIngredient.split(',');
                    let newIngredients = [...ingredientsList];
                    
                    ingredientList.forEach(item => {
                      const trimmedItem = item.trim().toLowerCase();
                      if (trimmedItem !== "" && !newIngredients.includes(trimmedItem)) {
                        newIngredients.push(trimmedItem);
                      }
                    });
                    
                    setIngredientsList(newIngredients);
                    setCurrentIngredient("");
                  }
                }
              }}
              placeholder="Enter ingredients separated by commas (e.g., chicken, rice, onions)"
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors pr-20"
              autoFocus
            />
            
            {/* File Upload Button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#0e4621] p-2 transition duration-300 flex items-center justify-center cursor-pointer"
              title="Upload image to identify ingredients"
            >
              <FontAwesomeIcon icon={faPaperclip} size="sm" />
            </label>
            
            {/* Plus Button */}
            <button
              type="button"
              onClick={() => {
                // Add ingredient logic
                if (currentIngredient.trim() !== "") {
                  const ingredientList = currentIngredient.split(',');
                  let newIngredients = [...ingredientsList];
                  
                  ingredientList.forEach(item => {
                    const trimmedItem = item.trim().toLowerCase();
                    if (trimmedItem !== "" && !newIngredients.includes(trimmedItem)) {
                      newIngredients.push(trimmedItem);
                    }
                  });
                  
                  setIngredientsList(newIngredients);
                  setCurrentIngredient("");
                }
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#0e4621] hover:text-[#0a3419] p-2 transition duration-300 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faPlus} size="sm" />
            </button>
          </div>

          {/* Image Preview (only show if image is uploaded) */}
          {imagePreview && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <img
                    src={imagePreview}
                    alt="Uploaded ingredients"
                    className="w-12 h-12 rounded object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Image uploaded</p>
                    <button
                      type="button"
                      onClick={handleAnalyzeImage}
                      disabled={isAnalyzingImage}
                      className="text-xs text-[#0e4621] hover:text-[#0a3419] disabled:opacity-50 underline"
                    >
                      {isAnalyzingImage ? "Analyzing image..." : "🔍 Scan for ingredients"}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-gray-400 hover:text-red-500 transition duration-300"
                >
                  <FontAwesomeIcon icon={faClose} size="sm" />
                </button>
              </div>
            </div>
          )}
          
          {/* Ingredients Tags */}
          {ingredientsList.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-2">
                {ingredientsList.map((ingredient, index) => (
                  <div key={index} className="bg-[#0e4621] text-white px-3 py-1.5 rounded-full flex items-center text-sm shadow-sm">
                    <span>{ingredient}</span>
                    <button 
                      type="button"
                      onClick={() => handleRemoveIngredient(index)}
                      className="ml-2 text-white hover:text-red-200 focus:outline-none"
                    >
                      <FontAwesomeIcon icon={faClose} size="xs" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Popular Ingredients Section */}
          <div className="bg-[#f7f9f8] border border-gray-100 rounded-md py-3 px-4 mt-2">
            <div className="flex justify-between items-center mb-2">
              <p className="text-[#0e4621] font-medium">Popular ingredients</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {['chicken', 'tomato', 'onion', 'garlic', 'pasta', 'rice', 'potato', 'egg'].map((ingredient) => {
                // Helper function to get color for ingredient buttons
                const getIngredientColor = (ingredient) => {
                  const colors = [
                    'bg-[#e8f5ec] text-[#0e4621]', // Green
                    'bg-[#f0f5ff] text-[#1e40af]', // Blue
                    'bg-[#fff4e6] text-[#c05621]', // Orange
                    'bg-[#faf5ff] text-[#6b46c1]', // Purple
                    'bg-[#f0ffff] text-[#047481]'  // Teal
                  ];
                  
                  // Use string hashing to assign consistent colors
                  let hash = 0;
                  for (let i = 0; i < ingredient.length; i++) {
                    hash = ingredient.charCodeAt(i) + ((hash << 5) - hash);
                  }
                  
                  return colors[Math.abs(hash) % colors.length];
                };
                
                const colorClass = getIngredientColor(ingredient);
                
                return (
                  <button
                    key={ingredient}
                    type="button"
                    onClick={() => {
                      if (!ingredientsList.includes(ingredient)) {
                        setIngredientsList([...ingredientsList, ingredient]);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:shadow-md ${
                      ingredientsList.includes(ingredient)
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed opacity-60"
                        : `${colorClass} hover:opacity-90`
                    } flex items-center`}
                    disabled={ingredientsList.includes(ingredient)}
                  >
                    {ingredient}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Search Button */}
          <button
            type="submit"
            disabled={ingredientsList.length === 0 || loading}
            className="w-full bg-[#0e4621] hover:bg-[#3e8e41] text-white py-2.5 rounded-md transition duration-300 flex items-center justify-center shadow-sm hover:shadow mt-3"
          >
            <FontAwesomeIcon icon={faSearch} className="mr-2" />
            <span className="font-medium">Find Recipes</span>
          </button>
          
          <p className="text-sm text-gray-600 mt-1">
            Enter ingredients you have to find recipes you can make.
          </p>
        </form>
      </div>
    )}
  </div>
)}

    </header>

    {/* Analysis Animation Overlay */}
    {showAnalysisAnimation && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center">
          <Player
            autoplay
            loop
            src={loader}
            style={{ height: '150px', width: '150px' }}
          />
          <div className="mt-4">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <FontAwesomeIcon icon={faMagicWandSparkles} className="text-[#0e4621] text-xl" />
              <h3 className="text-lg font-semibold text-gray-800">Analyzing Image</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Identifying ingredients from your image...
            </p>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default PublicMenuBar;

